import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "../UIElements/Icon";

const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

export const createDefaultPromotionSchedule = () =>
  DAYS.map(() => ({
    enabled: true,
    lapses: [{ open: { hour: 0, minute: 0 }, close: { hour: 23, minute: 59 } }],
  }));

const formatTime = (time) =>
  `${String(time?.hour ?? 0).padStart(2, "0")}:${String(
    time?.minute ?? 0,
  ).padStart(2, "0")}`;

const parseTimeFromInput = (timeString) => {
  if (!timeString) {
    return { hour: 0, minute: 0 };
  }

  const [hoursRaw, minutesRaw] = String(timeString).split(":");
  const hour = Number(hoursRaw);
  const minute = Number(minutesRaw);

  return {
    hour: Number.isFinite(hour) ? hour : 0,
    minute: Number.isFinite(minute) ? minute : 0,
  };
};

const validateLapses = (lapses, t) => {
  const safeLapses = Array.isArray(lapses) ? lapses : [];

  const sortedLapses = [...safeLapses].sort((a, b) => {
    const aMinutes = (a?.open?.hour ?? 0) * 60 + (a?.open?.minute ?? 0);
    const bMinutes = (b?.open?.hour ?? 0) * 60 + (b?.open?.minute ?? 0);
    return aMinutes - bMinutes;
  });

  for (let index = 0; index < sortedLapses.length - 1; index += 1) {
    const current = sortedLapses[index];
    const next = sortedLapses[index + 1];

    const currentEndMinutes =
      (current?.close?.hour ?? 0) * 60 + (current?.close?.minute ?? 0);
    const nextStartMinutes =
      (next?.open?.hour ?? 0) * 60 + (next?.open?.minute ?? 0);

    if (currentEndMinutes >= nextStartMinutes) {
      return {
        isValid: false,
        message: t("schedule.overlapError", {
          count: index + 1,
          close: formatTime(current.close),
          open: formatTime(next.open),
        }),
      };
    }
  }

  return { isValid: true, message: "" };
};

const PromotionRulesSchedule = ({ schedule, onScheduleChange }) => {
  const { t } = useTranslation("common");
  const days = [
    t("schedule.days.monday"),
    t("schedule.days.tuesday"),
    t("schedule.days.wednesday"),
    t("schedule.days.thursday"),
    t("schedule.days.friday"),
    t("schedule.days.saturday"),
    t("schedule.days.sunday"),
  ];
  const [localSchedule, setLocalSchedule] = useState(() => {
    if (Array.isArray(schedule) && schedule.length > 0) {
      return schedule;
    }
    return createDefaultPromotionSchedule();
  });

  const [activeCopyDay, setActiveCopyDay] = useState(null);
  const [copyTargets, setCopyTargets] = useState([]);

  useEffect(() => {
    if (Array.isArray(schedule) && schedule.length > 0) {
      setLocalSchedule(schedule);
      return;
    }

    setLocalSchedule(createDefaultPromotionSchedule());
  }, [schedule]);

  const dayErrors = useMemo(() => {
    return DAYS.map((_, dayIndex) => {
      const daySchedule = localSchedule?.[dayIndex];
      if (!daySchedule || !daySchedule.enabled) {
        return "";
      }

      const validation = validateLapses(daySchedule.lapses, t);
      return validation.isValid ? "" : validation.message;
    });
  }, [localSchedule, t]);

  const commitSchedule = (nextSchedule) => {
    setLocalSchedule(nextSchedule);
    if (onScheduleChange) {
      onScheduleChange(nextSchedule);
    }
  };

  const handleToggleDayEnabled = (dayIndex) => {
    const nextSchedule = (localSchedule || []).map((day, index) => {
      if (index !== dayIndex) {
        return day;
      }

      return {
        ...day,
        enabled: !day?.enabled,
      };
    });

    commitSchedule(nextSchedule);
  };

  // const handleAddLapse = (dayIndex) => {
  //   const nextSchedule = (localSchedule || []).map((day, index) => {
  //     if (index !== dayIndex) {
  //       return day;
  //     }

  //     const previousLapses = Array.isArray(day?.lapses) ? day.lapses : [];

  //     return {
  //       ...day,
  //       lapses: [
  //         ...previousLapses,
  //         { open: { hour: 0, minute: 0 }, close: { hour: 23, minute: 59 } },
  //       ],
  //     };
  //   });

  //   commitSchedule(nextSchedule);
  // };

  // const handleRemoveLapse = (dayIndex, lapseIndex) => {
  //   const day = localSchedule?.[dayIndex];
  //   const lapses = Array.isArray(day?.lapses) ? day.lapses : [];

  //   if (lapses.length <= 1) {
  //     return;
  //   }

  //   const nextSchedule = (localSchedule || []).map((currentDay, index) => {
  //     if (index !== dayIndex) {
  //       return currentDay;
  //     }

  //     return {
  //       ...currentDay,
  //       lapses: lapses.filter((_, idx) => idx !== lapseIndex),
  //     };
  //   });

  //   commitSchedule(nextSchedule);
  // };

  const handleUpdateLapse = (dayIndex, lapseIndex, field, value) => {
    const nextSchedule = (localSchedule || []).map((day, index) => {
      if (index !== dayIndex) {
        return day;
      }

      const previousLapses = Array.isArray(day?.lapses) ? day.lapses : [];

      return {
        ...day,
        lapses: previousLapses.map((lapse, idx) => {
          if (idx !== lapseIndex) {
            return lapse;
          }

          return {
            ...lapse,
            [field]: { ...value },
          };
        }),
      };
    });

    commitSchedule(nextSchedule);
  };

  const handleTimeChange = (dayIndex, lapseIndex, field, timeString) => {
    const time = parseTimeFromInput(timeString);
    handleUpdateLapse(dayIndex, lapseIndex, field, time);
  };

  const handleToggleCopyTarget = (targetIndex) => {
    setCopyTargets((previous) =>
      previous.includes(targetIndex)
        ? previous.filter((item) => item !== targetIndex)
        : [...previous, targetIndex],
    );
  };

  const handleApplyCopy = (sourceDayIndex) => {
    const source = localSchedule?.[sourceDayIndex];
    if (!source) {
      return;
    }

    const clonedLapses = (source.lapses || []).map((lapse) => ({
      open: { ...lapse.open },
      close: { ...lapse.close },
    }));

    const nextSchedule = [...(localSchedule || [])];

    copyTargets.forEach((targetIndex) => {
      if (targetIndex === sourceDayIndex) {
        return;
      }

      const target = nextSchedule[targetIndex] || { enabled: true, lapses: [] };

      nextSchedule[targetIndex] = {
        ...target,
        enabled: source.enabled,
        lapses: clonedLapses,
      };
    });

    commitSchedule(nextSchedule);
    setActiveCopyDay(null);
    setCopyTargets([]);
  };

  const handleCloseCopyPanel = () => {
    setActiveCopyDay(null);
    setCopyTargets([]);
  };

  return (
    <div className="ordering-discounts-schedule space-y-4 sm:space-y-6">
      {days.map((dayLabel, dayIndex) => {
        const daySchedule = localSchedule?.[dayIndex] || {
          enabled: true,
          lapses: [],
        };
        const lapses = Array.isArray(daySchedule.lapses)
          ? daySchedule.lapses
          : [];
        const validationError = dayErrors[dayIndex] || "";

        return (
          <div
            key={dayLabel}
            className="border-border bg-card rounded-lg border p-3 sm:p-4"
          >
            <div className="relative mb-3 flex items-center justify-between sm:mb-4">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <button
                  type="button"
                  onClick={() => handleToggleDayEnabled(dayIndex)}
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded border-2 ${
                    daySchedule.enabled
                      ? "border-primary bg-primary"
                      : "border-border bg-muted"
                  }`}
                  aria-label={t(
                    daySchedule.enabled
                      ? "schedule.disableDay"
                      : "schedule.enableDay",
                    { day: dayLabel },
                  )}
                >
                  {daySchedule.enabled ? (
                    <Icon
                      name="Check"
                      className="h-3 w-3 text-white sm:h-4 sm:w-4"
                    />
                  ) : null}
                </button>
                <span
                  className={`text-sm font-medium sm:text-base ${
                    daySchedule.enabled
                      ? "text-foreground"
                      : "text-muted-foreground"
                  }`}
                >
                  {dayLabel}
                </span>
              </div>

              <div className="flex items-center space-x-2">
                {/* {daySchedule.enabled && (
                  <button
                    type="button"
                    onClick={() => handleAddLapse(dayIndex)}
                    className="flex items-center space-x-1 text-primary hover:text-primary text-xs sm:text-sm"
                    aria-label={`Add time period for ${dayLabel}`}
                  >
                    <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Add Time Period</span>
                    <span className="sm:hidden">Add</span>
                  </button>
                )} */}

                <button
                  type="button"
                  onClick={() =>
                    setActiveCopyDay((previous) =>
                      previous === dayIndex ? null : dayIndex,
                    )
                  }
                  className="border-border text-muted-foreground hover:text-foreground flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded border p-1.5 sm:p-2"
                  aria-label={t("schedule.copyTimes")}
                  title={t("schedule.copyTimes")}
                >
                  <Icon name="Copy" className="h-3 w-3 sm:h-4 sm:w-4" />
                </button>
              </div>
            </div>

            {activeCopyDay === dayIndex && (
              <div className="border-border bg-card mt-3 rounded-lg border p-3">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    {t("schedule.copyTo")}
                  </p>
                  <button
                    type="button"
                    onClick={handleCloseCopyPanel}
                    className="border-border text-muted-foreground hover:text-foreground flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded border p-1.5"
                    aria-label={t("schedule.closeCopy")}
                  >
                    <Icon name="X" className="h-3 w-3 sm:h-4 sm:w-4" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {days.map((label, targetIndex) => {
                    if (targetIndex === dayIndex) {
                      return null;
                    }

                    const isChecked = copyTargets.includes(targetIndex);

                    return (
                      <button
                        key={label}
                        type="button"
                        onClick={() => handleToggleCopyTarget(targetIndex)}
                        className={`rounded border px-2 py-1.5 text-left text-[length:var(--font-size-product-ui)] ${
                          isChecked
                            ? "border-primary bg-primary text-white"
                            : "border-border bg-muted text-foreground"
                        }`}
                        aria-label={t("schedule.copyTarget", { day: label })}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>

                <div className="mt-3 flex justify-end">
                  <button
                    type="button"
                    onClick={() => handleApplyCopy(dayIndex)}
                    disabled={copyTargets.length === 0}
                    className="bg-primary hover:bg-accent text-primary-foreground rounded px-3 py-1.5 text-[length:var(--font-size-product-ui)] font-medium disabled:cursor-not-allowed disabled:opacity-50"
                    aria-label={t("schedule.applyCopy")}
                  >
                    {t("schedule.apply")}
                  </button>
                </div>
              </div>
            )}

            {daySchedule.enabled && (
              <div className="space-y-2 sm:space-y-3">
                {lapses.map((lapse, lapseIndex) => (
                  <div
                    key={`${dayLabel}-${lapseIndex}`}
                    className="bg-muted flex flex-col gap-2 rounded-lg p-2 sm:flex-row sm:items-center sm:gap-4 sm:p-3"
                  >
                    <div className="flex-1">
                      <label
                        htmlFor={`promotion-${dayIndex}-${lapseIndex}-open`}
                        className="text-muted-foreground mb-1 block text-[length:var(--font-size-product-ui)]"
                      >
                        {t("schedule.open")}
                      </label>
                      <input
                        id={`promotion-${dayIndex}-${lapseIndex}-open`}
                        type="time"
                        data-popup-owner="native"
                        value={formatTime(lapse.open)}
                        onChange={(event) =>
                          handleTimeChange(
                            dayIndex,
                            lapseIndex,
                            "open",
                            event.target.value,
                          )
                        }
                        className="border-border bg-card focus:ring-ring text-foreground w-full rounded border px-2 py-1.5 text-[length:var(--font-size-product-ui)] focus:ring-2 focus:outline-none sm:px-3 sm:py-2"
                        aria-label={t("schedule.openTime", {
                          day: dayLabel,
                          count: lapseIndex + 1,
                        })}
                        aria-invalid={Boolean(validationError) || undefined}
                        aria-describedby={
                          validationError
                            ? `promotion-${dayIndex}-schedule-error`
                            : undefined
                        }
                      />
                    </div>

                    <div className="flex-1">
                      <label
                        htmlFor={`promotion-${dayIndex}-${lapseIndex}-close`}
                        className="text-muted-foreground mb-1 block text-[length:var(--font-size-product-ui)]"
                      >
                        {t("schedule.close")}
                      </label>
                      <input
                        id={`promotion-${dayIndex}-${lapseIndex}-close`}
                        type="time"
                        data-popup-owner="native"
                        value={formatTime(lapse.close)}
                        onChange={(event) =>
                          handleTimeChange(
                            dayIndex,
                            lapseIndex,
                            "close",
                            event.target.value,
                          )
                        }
                        className="border-border bg-card focus:ring-ring text-foreground w-full rounded border px-2 py-1.5 text-[length:var(--font-size-product-ui)] focus:ring-2 focus:outline-none sm:px-3 sm:py-2"
                        aria-label={t("schedule.closeTime", {
                          day: dayLabel,
                          count: lapseIndex + 1,
                        })}
                        aria-invalid={Boolean(validationError) || undefined}
                        aria-describedby={
                          validationError
                            ? `promotion-${dayIndex}-schedule-error`
                            : undefined
                        }
                      />
                    </div>

                    {/* <div className="flex items-center pt-4">
                      <button
                        type="button"
                        onClick={() => handleRemoveLapse(dayIndex, lapseIndex)}
                        disabled={lapses.length <= 1}
                        className={`p-1.5 sm:p-2 transition-colors ${
                          lapses.length > 1
                            ? 'text-destructive hover:text-destructive cursor-pointer'
                            : 'text-muted-foreground cursor-not-allowed opacity-50'
                        }`}
                        aria-label={`Remove time period from ${dayLabel}`}
                        title={
                          lapses.length > 1
                            ? 'Remove time period'
                            : 'At least one time period required'
                        }
                      >
                        <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                    </div> */}
                  </div>
                ))}

                {validationError && (
                  <div
                    id={`promotion-${dayIndex}-schedule-error`}
                    role="alert"
                    className="border-destructive text-destructive rounded border bg-[var(--error-bg)] p-2 text-xs sm:text-sm"
                  >
                    {validationError}
                  </div>
                )}
              </div>
            )}

            {!daySchedule.enabled && (
              <p className="text-muted-foreground mt-3 text-xs italic">
                {t("schedule.closedOn", { day: dayLabel })}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PromotionRulesSchedule;
